/**
 * Image Proxy Service
 *
 * Two-phase approach for handling external product images:
 * Phase 1: Quick preview via proxy (bypasses CORS)
 * Phase 2: Permanent storage in Supabase Storage (reliable long-term URLs)
 */

import { getAuthenticatedSupabaseClient } from '@/integrations/supabase/client';
import { buildSupabaseAuthHeaders } from '@/lib/auth';
import { logger } from '@/lib/logger';

export class ImageProxyService {
  /**
   * Phase 1: Get a proxied URL for quick preview (bypasses CORS)
   * Fetches image through authenticated edge function and returns a blob URL
   */
  static async getProxiedUrl(originalUrl: string): Promise<string> {
    try {
      // Skip proxy for Supabase URLs (already hosted by us)
      if (originalUrl.includes('supabase.co')) {
        return originalUrl;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      if (!supabaseUrl) {
        logger.warn('VITE_SUPABASE_URL not configured');
        return originalUrl;
      }

      logger.log('Fetching proxied image with authenticated headers:', originalUrl);

      // Fetch image through edge function with authentication
      const encodedUrl = encodeURIComponent(originalUrl);
      const proxyUrl = `${supabaseUrl}/functions/v1/proxy-image?url=${encodedUrl}`;

      const response = await fetch(proxyUrl, {
        headers: await buildSupabaseAuthHeaders(
          {
            Accept: 'image/*',
          },
          { requireJwt: true },
        ),
      });

      if (!response.ok) {
        logger.warn('Proxy image fetch failed:', response.status, '- falling back to direct URL');
        return originalUrl;
      }

      // Convert response to blob
      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      logger.log('Created blob URL for proxied image');
      return blobUrl;
    } catch (error) {
      logger.warn('Failed to proxy image, using direct URL:', error);
      return originalUrl; // Fallback to original URL
    }
  }

  /**
   * Phase 2: Download and cache image permanently in Supabase Storage
   * Returns a permanent Supabase Storage URL
   */
  static async downloadAndCache(
    url: string,
    metadata?: { brand?: string; model?: string },
  ): Promise<string> {
    try {
      // Skip caching for Supabase URLs (already in our storage)
      if (url.includes('supabase.co/storage')) {
        return url;
      }

      const supabase = await getAuthenticatedSupabaseClient();

      logger.log('Caching product image:', { url, metadata });

      const { data, error } = await supabase.functions.invoke('cache-product-image', {
        body: { url, metadata },
      });

      if (error) {
        logger.warn('Failed to cache image, using original URL:', error);
        // If caching fails, return original URL as fallback
        return url;
      }

      if (!data?.publicUrl) {
        logger.warn('No public URL returned from cache-product-image');
        return url;
      }

      logger.log('Image cached successfully:', data.publicUrl);
      return data.publicUrl;
    } catch (error) {
      logger.warn('Failed to download and cache image, using original URL:', error);
      return url; // Fallback to original URL
    }
  }

  /**
   * Check if we're in development mode
   */
  static isDevelopment(): boolean {
    return window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
  }

  /**
   * Get placeholder image for failed loads
   */
  static getPlaceholderImage(): string {
    // SVG placeholder image
    return 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgdmlld0JveD0iMCAwIDQwMCA0MDAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSI0MDAiIGhlaWdodD0iNDAwIiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMDAgMTIwQzE3OC41IDEyMCAxNjEgMTM3LjUgMTYxIDE1OVYyNDFDMTYxIDI2Mi41IDE3OC41IDI4MCAyMDAgMjgwQzIyMS41IDI4MCAyMzkgMjYyLjUgMjM5IDI0MVYxNTlDMjM5IDEzNy41IDIyMS41IDEyMCAyMDAgMTIwWiIgZmlsbD0iIzlCOUNBMyIvPgo8cGF0aCBkPSJNMTgwIDIwMEgxNjBWMjIwSDE4MFYyMDBaIiBmaWxsPSIjOUI5Q0EzIi8+CjxwYXRoIGQ9Ik0yNDAgMjAwSDIyMFYyMjBIMjQwVjIwMFoiIGZpbGw9IiM5QjlDQTMiLz4KPC9zdmc+';
  }
}

export default ImageProxyService;
